import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActionIcon, Box, Button, Group, Menu, Modal, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { IconDots, IconEdit, IconPencil, IconPlus, IconTemplate, IconTrash } from '@tabler/icons-react';
import { flowsApi, type Flow } from '@/services/flows.api';
import { EditFlowModal } from '../components/EditFlowModal';
import { CopyFlowModal } from '../components/CopyFlowModal';
import { UseTemplateModal } from '../components/UseTemplateModal';
import { useTemplates } from '../hooks/useTemplates';

/** Library › Templates: every template in the workspace, and what to do with each. */
export default function TemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { templates, isLoading, createTemplate } = useTemplates();
  const [using, setUsing] = useState<Flow | null>(null);
  const [editing, setEditing] = useState<Flow | null>(null);
  const [deleting, setDeleting] = useState<Flow | null>(null);
  const [creating, setCreating] = useState(false);

  const trash = useMutation({
    mutationFn: (id: string) => flowsApi.trashFlow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      queryClient.invalidateQueries({ queryKey: ['flows-trashed'] });
      setDeleting(null);
    },
  });

  return (
    <Box p="xl" px={32}>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={2} c="#2A3140">Templates</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Starting points for new flows. A flow made from a template is its own copy.
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={16} />}
          style={{ backgroundColor: '#415A91' }}
          radius="md"
          onClick={() => setCreating(true)}
        >
          New template
        </Button>
      </Group>

      {templates.length === 0 && !isLoading ? (
        <Paper p="xl" radius="md" withBorder ta="center">
          <IconTemplate size={48} style={{ color: '#ADB5BD' }} />
          <Text size="lg" fw={500} mt="md" c="#2A3140">No templates yet</Text>
          <Text size="sm" c="dimmed" mt={4}>
            Save any flow as a template from its ⋯ menu on the dashboard, or start a blank one.
          </Text>
          <Button mt="lg" variant="light" color="blue" leftSection={<IconPlus size={14} />} onClick={() => setCreating(true)}>
            Create your first template
          </Button>
        </Paper>
      ) : (
        <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Used</Table.Th>
                <Table.Th>Updated</Table.Th>
                <Table.Th w={220}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {templates.map((template) => (
                <Table.Tr key={template.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{template.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={2}>{template.description || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{template.templateUseCount ?? 0}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{new Date(template.updatedAt).toLocaleDateString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Button size="xs" variant="light" onClick={() => setUsing(template)}>
                        Use
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconEdit size={14} />}
                        onClick={() => navigate(`/flows/${template.id}`)}
                      >
                        Edit
                      </Button>
                      <Menu position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" aria-label={`More actions for ${template.name}`}>
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setEditing(template)}>
                            Edit details
                          </Menu.Item>
                          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => setDeleting(template)}>
                            Move to Trash
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <UseTemplateModal template={using} onClose={() => setUsing(null)} />
      <EditFlowModal opened={!!editing} onClose={() => setEditing(null)} flow={editing} />
      <CopyFlowModal
        opened={creating}
        onClose={() => setCreating(false)}
        title="New template"
        confirmLabel="Create template"
        defaultName="Untitled template"
        onConfirm={async (name) => {
          const template = await createTemplate.mutateAsync({ name });
          navigate(`/flows/${template.id}`);
        }}
      />

      <Modal opened={!!deleting} onClose={() => setDeleting(null)} title="Move template to trash?">
        <Stack>
          <Text size="sm">
            "{deleting?.name}" moves to the trash. Flows already made from it are separate copies and are not affected.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button color="red" loading={trash.isPending} onClick={() => deleting && trash.mutate(deleting.id)}>
              Move to Trash
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
